import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';

import { useGetPublicKanbanCustomField } from '~/components/KanbanView/KanbanColumnsManagers/DndKanbanManager/useUpdateKanbanItemPosition/useGetPublicKanbanCustomField';
import { KanbanView } from '~/components/KanbanView/KanbanView';
import { KanbanProvider, type KanbanProviderProps } from '~/components/KanbanView/Providers/KanbanProvider';
import { KanbanNoCustomFieldPermissions } from '~/components/PublicBoard/components/PublicKanbanView/components/KanbanNoCustomFieldPermissions';
import { PublicKanbanNullState } from '~/components/PublicBoard/components/PublicKanbanView/components/PublicKanbanNullState';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { useKanbanOffsetContext } from '~/components/v2/components/shared/kanban/KanbanOffsetContextProvider';
import { useScrollElementContext } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { currentKanbanGroupByCustomFieldIdSelector } from '~/store/configViews/selectors';
import { useGetPublicCustomFieldOptions } from '~/swr-hooks/customFields/useGetPublicCustomFieldOptions';
import { useUpdatePublicItemsCustomFieldValue } from '~/utils/CustomFields/useUpdatePublicItemsCustomFieldValue';
import { canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export const PublicKanbanView = memo(() => {
  const { kanbanOffset } = useKanbanOffsetContext();
  const { permissions } = usePublicPermissions();
  const canViewCustomFields = canSeeBoardCustomFields(permissions);
  const { customFields } = usePublicCustomFieldsContext();
  const baseCustomField = useGetPublicKanbanCustomField();
  const customFieldId = useSelector(currentKanbanGroupByCustomFieldIdSelector);
  const { data: options } = useGetPublicCustomFieldOptions({ customFieldId });
  const { workspaceId } = usePublicWorkspace();
  const { updatePublicItemsCustomFieldValue } = useUpdatePublicItemsCustomFieldValue();
  const { scrollElement } = useScrollElementContext();

  const { height: containerHeight = 0 } = useResizeDetector({
    targetRef: { current: scrollElement },
    handleWidth: false,
  });

  const onUpdateItems = useCallback<KanbanProviderProps['onUpdateItems']>(
    async (params) => {
      await updatePublicItemsCustomFieldValue({
        ...params,
        trackingLocation: 'kanban-view',
      });
    },
    [updatePublicItemsCustomFieldValue],
  );

  const calculatedHeight = containerHeight - kanbanOffset - 48; // TODO: remove hardcoded value @Andy will take care of this

  return (
    <KanbanProvider customFieldValues={options?.data} onUpdateItems={onUpdateItems} workspaceId={workspaceId}>
      <KanbanView
        NullState={canViewCustomFields ? PublicKanbanNullState : KanbanNoCustomFieldPermissions}
        tableHeaderTopPos={kanbanOffset}
        customFields={customFields}
        baseCustomField={baseCustomField}
        customFieldValues={options?.data}
        canViewCustomFields={canViewCustomFields}
        style={{
          height: `${calculatedHeight}px`,
        }}
      />
    </KanbanProvider>
  );
});

PublicKanbanView.displayName = 'PublicKanbanView';
